import type { SortingQuery } from './sort-info.type'
import { extractQuery } from '../utils'
import { z } from '../utils/zod-extend'
import { actionConfigPageSchema, actionConfigUserSchema } from './action.type'
import { campaignQueryPresets } from './campaign.type'
import { stringArrayParamSchema } from './http-param.type'
import { idSchema } from './id.type'
import { mediaFileTypes } from './media-file.type'
import { offsetPaginationParamSchema } from './page-info.type'
import { pageMetaSchema } from './page-meta.type'
import { sortingParamSchema } from './sort-info.type'
import { themeSchema } from './theme.type'

export const popupButtonTypes = ['round', 'square'] as const
export type PopupButtonType = typeof popupButtonTypes[number]

export const popupDisplayPositions = ['bottom-left', 'bottom-right'] as const
export type PopupDisplayPosition = typeof popupDisplayPositions[number]

export const popupImageTypes = ['titleBg', 'icon'] as const
export type PopupImageType = typeof popupImageTypes[number]

// TODO: account単位設定可能にする
export const defaultPopupSuspendSeconds = 28800 // 8時間
const suspendSecondsSchema = z.preprocess(
  v => (v === '' || v === null || v === undefined) ? null : Number(v),
  z.number().min(0).nullable(),
)
export const popupPositionConfigSchema = z.object({
  displayPosition: z.enum(popupDisplayPositions).optional(),
  horizontal: z.number().min(0).max(100).optional(),
  vertical: z.number().min(0).max(600).optional(),
})
export type PopupPositionConfig = z.infer<typeof popupPositionConfigSchema>

export const popupSquareConfigSchema = z.object({
  buttonType: z.literal('square'),
  title: z.string().max(200).optional(),
  description: z.string().max(2048).optional(),
  showCloseButton: z.boolean().optional(),
  imageUrl: z.string().max(2048).optional(),
  imageUid: z.string().max(255).optional(),
  imageType: z.enum(popupImageTypes).optional(),
  mediaFileType: z.enum(mediaFileTypes).optional(),
  videoUrl: z.string().max(2048).optional(),
  videoUid: z.string().max(255).optional(),
  theme: themeSchema.optional(),
}).merge(popupPositionConfigSchema)
export type PopupSquareConfig = z.infer<typeof popupSquareConfigSchema>

export const presetIcons = ['edit', 'talk'] as const
export type PresetIcon = typeof presetIcons[number]

export const popupRoundConfigSchema = z.object({
  buttonType: z.literal('round'),
  title: z.string().max(200).optional(),
  description: z.string().max(2048).optional(),
  showCloseButton: z.boolean().optional(),
  theme: themeSchema.optional(),
  presetIcon: z.enum(presetIcons).optional(),
}).merge(popupPositionConfigSchema)
export type PopupRoundConfig = z.infer<typeof popupRoundConfigSchema>
export type PopupConfig = PopupSquareConfig | PopupRoundConfig

/**
 * popup - デザイン 四角
 */
export const createPopupSquareSchema = z.object({
  name: z.string().trim().min(1).max(256),
  configJson: popupSquareConfigSchema.optional(),
  suspendSeconds: suspendSecondsSchema.optional(),
})
export type CreatePopupSquareValues = z.infer<typeof createPopupSquareSchema>

/**
 * popup - デザイン 丸
 */
export const createPopupRoundSchema = z.object({
  name: z.string().trim().min(1).max(256),
  configJson: popupRoundConfigSchema.optional(),
  suspendSeconds: suspendSecondsSchema.optional(),
})
export type CreatePopupRoundValues = z.infer<typeof createPopupRoundSchema>

/**
 * popup - キャンペーン選択
 */
export const createPopupCampaignSelectionContentSchema = z.object({
  campaignSelectionContentId: idSchema,
})
export type CreatePopupCampaignSelectionContentValues = z.infer<typeof createPopupCampaignSelectionContentSchema>

/**
 * popup - 配信設定 (ポップアップ設定)
 */
export const updatePopupDeliverySchema = z.object({
  enabled: z.coerce.boolean(),
  isAllowedConcurrentDisplay: z.coerce.boolean(),
  suspendSeconds: suspendSecondsSchema.optional(),
})
export type UpdatePopupDeliveryValues = z.infer<typeof updatePopupDeliverySchema>

/**
 * popup - 配信設定 (ページごとの出し分け設定)
 */
export const updatePopupActionConfigOfPageSchema = z.object({
  funnel: z.object({
    page: actionConfigPageSchema,
  }),
})
export type UpdatePopupActionConfigOfPageValues = z.infer<typeof updatePopupActionConfigOfPageSchema>

/**
 * popup - 配信設定 (ユーザーごとの出し分け設定)
 */
export const updatePopupActionConfigOfUserSchema = z.object({
  funnel: z.object({
    user: actionConfigUserSchema,
  }),
})
export type UpdatePopupActionConfigOfUserValues = z.infer<typeof updatePopupActionConfigOfUserSchema>

/**
 * popup - その他設定
 */
export const updatePopupSettingsSchema = z.object({
  name: z.string().trim().min(1).max(256),
  description: z.string().max(2048),
})
export type UpdatePopupSettingsValues = z.infer<typeof updatePopupSettingsSchema>

/**
 * popup - 作成
 */
export const createPopupSchema = z.union([
  createPopupSquareSchema,
  createPopupRoundSchema,
])
export type CreatePopupValues
  = | CreatePopupSquareValues
    | CreatePopupRoundValues

/**
 * popup - 修正
 */
export const updatePopupSchema = z.union([
  updatePopupSettingsSchema,
  createPopupSchema,
  createPopupCampaignSelectionContentSchema,
  updatePopupDeliverySchema,
])
export type UpdatePopupValues
  = | CreatePopupValues
    | CreatePopupCampaignSelectionContentValues
    | UpdatePopupDeliveryValues
    | UpdatePopupSettingsValues

export const popupQueryPresets = campaignQueryPresets
export type PopupQueryPresets = typeof popupQueryPresets[number]

export const isPopupQueryPresets = (v: any): v is PopupQueryPresets => popupQueryPresets.includes(v)

export const popupDeliverySorts: SortingQuery = {
  sortFields: ['updatedAt'],
  sortDirections: ['asc'],
}
export const popupDeliverySortConditions = extractQuery(popupDeliverySorts).sorts

export const searchPopupQuerySchema = z.object({
  name: z.string().max(255).optional(),
  campaignId: z.string().optional(),
  preset: z.enum(campaignQueryPresets).optional(),
}).merge(sortingParamSchema).openapi('SearchPopupQuery')
export type SearchPopupQuery = z.infer<typeof searchPopupQuerySchema>

export const searchPopupWithOffsetQuerySchema = searchPopupQuerySchema.merge(offsetPaginationParamSchema)
export type SearchPopupWithOffsetQuery = z.infer<typeof searchPopupWithOffsetQuerySchema>

export const getPopupOptions = z.object({
  withCampaigns: z.coerce.boolean().optional(),
})
export type GetPopupOptions = z.infer<typeof getPopupOptions>

export const searchUserPopupQuerySchema = z.object({
  excludeIds: stringArrayParamSchema.optional(),
}).openapi('SearchUserPopupQuery')
export type SearchUserPopupQuery = z.infer<typeof searchUserPopupQuerySchema>

export const bootPopupListSchema = z.object({
  // query: searchUserPopupQuerySchema.nullish(), // 使っていると思ったら使ってなかった
  meta: pageMetaSchema.nullish(),
})
export type BootPopupListValues = z.infer<typeof bootPopupListSchema>
